import React from 'react';
import styled from 'styled-components';
import { Tooltip } from '@mui/material';
import { GridRenderCellParams } from '@mui/x-data-grid-pro';
import { NxObjectType } from '@warehouse/object-browser/core';
import { Chip } from '@warehouse/shared/legacy-ui';
import { TypoLink } from '@warehouse/shared/ui';
import { BrokerJob, NX_PURGE_SERVICE_UUID } from '@warehouse/object-browser/feature-broker';
import { ResolvedJobReference, shortETag } from '@warehouse/service-log/core';
import { ObjectBrowserDeepLinks } from '@warehouse/service-log/domain';
import { useJobReference } from './use-job-reference';

const Wrapper = styled('div')(({ theme }) => ({
	alignItems: 'center',
	display: 'flex',
	gap: theme.spacing(1),
	minWidth: 0,
}));

const Ellipsis = styled('span')({
	display: 'block',
	minWidth: 0,
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
});

/** The Object Browser's link look (TypoLink), shrinkable so the eTag ellipsizes inside the cell. */
const CellLink = styled(TypoLink)({
	minWidth: 0,
});

const NO_REFERENCE = '--';

/**
 * ETag column: the eTag of what the job ran on, whatever the job (see JobReferenceParser).
 * - eTag-keyed job: the eTag from its id;
 * - uuid-keyed job: its object's eTag, once the Inventory resolved it (folders have none).
 * The eTag (shortened, in full on hover) links to the Object Browser's All Storage search on it, which lists every
 * copy of that content; a chip says how many copies the Inventory holds. A Delete job's link also shows offline copies
 * as such (Offline column, "any" Offline filter), since that is what the job left behind.
 * Clicks stop at the cell so they never count as a row click.
 */
export function ObjectCell({ row }: GridRenderCellParams<BrokerJob, string>) {
	const reference = useJobReference(row);
	const { eTag, bucketId, copyCount } = eTagOf(reference);

	if (!eTag) {
		if (reference.kind === 'object' && reference.object === null) {
			return (
				<Tooltip title="This object is no longer in the Inventory">
					<Ellipsis>{NO_REFERENCE}</Ellipsis>
				</Tooltip>
			);
		}
		return <Ellipsis>{NO_REFERENCE}</Ellipsis>;
	}

	return (
		<Wrapper>
			{/* No copy left anywhere: no bucket to open the Object Browser on, and its search would find nothing anyway. */}
			{bucketId ? (
				<CellLink
					to={ObjectBrowserDeepLinks.forETag({
						bucketId,
						eTag,
						showOffline: row.serviceUUID === NX_PURGE_SERVICE_UUID,
					})}
					onClick={(event) => event.stopPropagation()}
					aria-label={`Search ${eTag} in the Object Browser`}
				>
					<Ellipsis title={eTag}>{shortETag(eTag)}</Ellipsis>
				</CellLink>
			) : (
				<Ellipsis title={eTag}>{shortETag(eTag)}</Ellipsis>
			)}
			{copyCount !== undefined && <Chip label={`${copyCount} ${copyCount === 1 ? 'copy' : 'copies'}`} />}
		</Wrapper>
	);
}

/**
 * The eTag to show, a bucket holding a copy of it (the Object Browser link opens on a bucket), and how many copies
 * exist once known.
 */
function eTagOf(reference: ResolvedJobReference): { eTag?: string; bucketId?: string; copyCount?: number } {
	switch (reference.kind) {
		case 'object':
			return reference.object?.itemType === NxObjectType.File
				? { eTag: reference.object.eTag, bucketId: reference.object.bucketId, copyCount: reference.copies?.length }
				: {};
		case 'content':
			return { eTag: reference.eTag, bucketId: reference.copies?.[0]?.bucketId, copyCount: reference.copies?.length };
		default:
			return {};
	}
}
